import type { LoginFormData } from "@shared/schemas";
import { loginSchema } from "@shared/schemas";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useLogin } from "@/api/mutations/auth";
import { useOAuthLogin } from "@/api/mutations/register";
import { usePublicConfig } from "@/api/queries/config";
import { FormDivider, OAuthButtons } from "@/components/auth";
import { PasswordInput } from "@/components/auth/PasswordInput";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/hooks/use-auth";
import { usePageMeta } from "@/hooks/use-page-meta";
import { ApiError } from "@/lib/api-client";
import { zodResolver } from "@/lib/zod-resolver";
import { useAuthStore } from "@/stores/auth-store";
import { type ToastType, useToastStore } from "@/stores/toast-store";

export const Route = createFileRoute("/_auth/login")({
	validateSearch: z.object({
		redirect: z.string().optional(),
		error: z.string().optional(),
		// TanStack Router parst Suchwerte mit JSON.parse: aus "?deleted=1" wird die
		// Zahl 1, nicht der String "1". Bei "?error=logout" scheitert JSON.parse und
		// der Wert bleibt ein String -- deshalb faellt das dort nicht auf. Beide
		// Formen annehmen, sonst wirft validateSearch und die Login-Seite zeigt
		// statt des Formulars einen Fehlerbildschirm.
		deleted: z.union([z.string(), z.number()]).optional(),
	}),
	component: LoginPage,
});

/**
 * Meldungen, mit denen andere Stellen auf die Login-Seite verweisen. Als eigene
 * Funktion, damit die Zuordnung ohne Router-Lauf prüfbar bleibt.
 */
export function loginNotice(search: {
	error?: string;
	deleted?: string | number;
}): { type: ToastType; message: string } | undefined {
	if (String(search.deleted) === "1") {
		return {
			type: "info",
			message: "Dein Konto wurde gelöscht. Alle deine Daten sind entfernt.",
		};
	}

	if (search.error === "oauth") {
		return {
			type: "error",
			message: "OAuth-Login fehlgeschlagen. Bitte versuche es erneut.",
		};
	}

	if (search.error === "logout") {
		return {
			type: "error",
			message:
				"Die Abmeldung konnte auf dem Server nicht bestätigt werden. Auf diesem Gerät bist du abgemeldet. Schließe zur Sicherheit den Browser.",
		};
	}

	// Beide Werte setzt der Verify-Handler in server/app.ts, wenn der Link aus
	// der Registrierungsmail nicht mehr trägt.
	if (search.error === "expired") {
		// Bewusst ohne Handlungsaufforderung: Ein Kunde ohne bestätigte Adresse
		// kommt nicht durch den Login (ApiV3CustomerLoginController wirft ihn mit
		// 401 wieder hinaus), und einen Weg, sich einen neuen Link schicken zu
		// lassen, gibt es derzeit nicht.
		return {
			type: "warning",
			message: "Dieser Bestätigungslink ist abgelaufen und lässt sich nicht mehr verwenden.",
		};
	}

	if (search.error === "verify-failed") {
		return {
			type: "error",
			message:
				"Die Bestätigung deiner Registrierung ist fehlgeschlagen. Bitte versuche es erneut oder wende dich an den Support.",
		};
	}

	return undefined;
}

function LoginPage() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const { rememberedEmail } = useAuth();
	const { setPendingSteps } = useAuthStore();
	const { addToast } = useToastStore();

	const loginMutation = useLogin();
	const oauthLoginMutation = useOAuthLogin();
	const { data: config } = usePublicConfig();

	usePageMeta({
		title: config ? `Login - ${config.seo.defaultTitle}` : undefined,
		noIndex: true,
	});

	const oauthProviders = config?.oauthProviders ?? [];

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			email: rememberedEmail ?? "",
			password: "",
		},
	});

	const { error: searchError, deleted: searchDeleted } = search;

	useEffect(() => {
		const notice = loginNotice({ error: searchError, deleted: searchDeleted });
		if (notice) {
			addToast(notice);
		}
	}, [searchError, searchDeleted, addToast]);

	async function onSubmit(data: LoginFormData) {
		try {
			const result = await loginMutation.mutateAsync(data);

			// Das hasUser-Cookie setzt useLogin bereits in onSuccess, bevor mutateAsync
			// auflöst — der Auth-Guard sieht es also schon beim Navigieren.
			if (result.steps) {
				setPendingSteps(result.steps);
				navigate({ to: "/register-steps" });
				return;
			}

			navigate({ to: search.redirect ?? "/search" });
		} catch {
			// Fehler wird über loginMutation.error angezeigt
		}
	}

	function handleOAuthLogin(provider: string) {
		oauthLoginMutation.mutate(provider, {
			onSuccess: (data) => {
				if (data.redirectUrl) {
					window.location.href = data.redirectUrl;
				}
			},
			onError: () => {
				addToast({
					type: "error",
					message: "OAuth-Login konnte nicht gestartet werden.",
				});
			},
		});
	}

	const loginError =
		loginMutation.error instanceof ApiError
			? loginMutation.error.message
			: loginMutation.error?.message;

	return (
		<div className="space-y-6">
			<div className="text-center">
				<h1 className="text-2xl font-bold text-text">Willkommen zurück</h1>
				<p className="mt-2 text-base text-text-secondary">Melde dich an um fortzufahren</p>
			</div>

			<form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
				<Input
					{...register("email")}
					type="email"
					label="E-Mail"
					placeholder="deine@email.de"
					error={errors.email?.message}
					autoComplete="email"
					fullWidth
				/>

				<div>
					<PasswordInput
						{...register("password")}
						label="Passwort"
						placeholder="Dein Passwort"
						error={errors.password?.message}
						autoComplete="current-password"
						fullWidth
					/>
					<div className="mt-1 text-right">
						<Link to="/forgot-password" className="text-base text-primary hover:underline">
							Passwort vergessen?
						</Link>
					</div>
				</div>

				<Button type="submit" variant="primary" fullWidth loading={loginMutation.isPending}>
					Anmelden
				</Button>

				{loginError && (
					<p className="text-error text-base text-center" role="alert">
						{loginError}
					</p>
				)}
			</form>

			{oauthProviders.length > 0 && (
				<>
					<FormDivider />
					<OAuthButtons
						mode="login"
						providers={oauthProviders}
						onProviderClick={handleOAuthLogin}
						loading={oauthLoginMutation.isPending ? oauthLoginMutation.variables : null}
						disabled={loginMutation.isPending}
					/>
				</>
			)}

			<div className="text-center">
				<span className="text-base text-text-secondary">Noch kein Account? </span>
				<Link to="/register" className="text-base text-primary hover:underline">
					Jetzt registrieren
				</Link>
			</div>
		</div>
	);
}
